import { WarningAmberOutlined } from "@mui/icons-material"
import { Box, Stack, Typography } from "@mui/material"
import { FC, ReactNode } from "react"

export type CalloutSeverity = "severe" | "advisory" | "info"

/**
 * A job-level callout on the migration page.
 *
 *  - `severe` — red. Something is wrong and an operator is affected RIGHT NOW
 *    (occupied doors that cannot be opened, a flip that will never run).
 *  - `advisory` — muted blue/slate. Something to know; it resolves on its own.
 *  - `info` — muted slate, no warning icon. Plain state, not a problem.
 *
 * Never a yellow/amber FILL — repo convention is warning ICON only, because amber
 * fills read as errors at a glance and this page has real errors to reserve colour
 * for. (The MUI icon is named `WarningAmberOutlined`; its colour is overridden.)
 *
 * Extracted from `Sstv2MigrationJobProgress` 2026-07-29 so the attempt-state and
 * ingestion-DLQ panels raise callouts that look identical to the progress
 * panel's — three visual dialects of "something is wrong" on one page is how a
 * severe signal gets ignored.
 */
export const Sstv2MigrationCallout: FC<{
  severity: CalloutSeverity
  title: string
  hint: ReactNode
  /** Optional trailing action (e.g. the OLAP recompute button). */
  action?: ReactNode
}> = ({ severity, title, hint, action }) => {
  const severe = severity === "severe"
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="flex-start"
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: severe ? "rgba(198, 40, 40, 0.06)" : "rgba(35, 66, 133, 0.06)",
        border: "1px solid",
        borderColor: severe
          ? "rgba(198, 40, 40, 0.28)"
          : "rgba(35, 66, 133, 0.18)",
      }}
    >
      {severity !== "info" && (
        <WarningAmberOutlined
          fontSize="small"
          sx={{
            color: severe ? "error.main" : "info.main",
            mt: "1px",
            flexShrink: 0,
          }}
        />
      )}
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {hint}
        </Typography>
        {action && <Box sx={{ mt: 1 }}>{action}</Box>}
      </Box>
    </Stack>
  )
}

export default Sstv2MigrationCallout
